import React, { useState, useEffect } from "react";
import { redirect, useNavigate } from "react-router-dom";
import styles from "./reg.module.scss";
import Icon from "../../components/Icon";
import SignUpLogo from "../../components/SignUpLogo";
import { placeholder } from "../../constants/images";
import Button from "../../components/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Container, Inputs, Label } from "./components/Inputs";
import { faCameraAlt } from "@fortawesome/free-solid-svg-icons";
import { DatePicker } from "../../components/DatePicker";
import useLanguages from "../../context/LanguagesContext";
import { toast } from "sonner";

interface ApiResponse {
  message: string;
  statusCode: number;
}

type LanguageEntry = {
  language: string;
  proficiency: string;
};

type UserData = {
  firstName: string;
  lastName: string;
  otherNames: string;
  email: string;
  dateOfBirth: string;
  aboutYou: string;
  languagesSpoken: LanguageEntry[];
  languagesTaught: LanguageEntry[];
};

const defaultParameters: UserData = {
  firstName: "",
  lastName: "",
  otherNames: "",
  email: "",
  dateOfBirth: "",
  aboutYou: "",
  languagesSpoken: [],
  languagesTaught: [],
};

export default function RegisterForm() {
  const [userData, setUserData] = useState<UserData>(defaultParameters);

  const { languages_taught } = useLanguages();
  const [profileImage, setProfileImage] = useState<string>("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [dateValue, setDateValue] = useState("");
  const [selection, setSelection] = useState({ language: "", proficiency: "" });
  const [activePicker, setActivePicker] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [selection2, setSelection2] = useState({
    language: "",
    proficiency: "",
  });

  useEffect(() => {
    setUserData((prev) => ({
      ...prev,
      dateOfBirth: dateValue,
    }));

    setActivePicker(false);
  }, [dateValue]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setUserData({ ...userData, [e.target.name]: e.target.value });
  };

  const handleImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setProfileImage(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!profileImage) {
      toast.error("Please upload a profile photo before submitting.");
      return;
    }

    if (!userData.languagesSpoken || userData.languagesSpoken.length === 0) {
      return toast.error(
        "Please add at least one spoken language with proficiency."
      );
    }

    if (!userData.languagesTaught || userData.languagesTaught.length === 0) {
      return toast.error(
        "Please add at least one taught language with proficiency."
      );
    }

    setIsPending(true);

    const formData = new FormData();
    Object.entries(userData).forEach(([key, value]) => {
      formData.append(
        key,
        Array.isArray(value) ? JSON.stringify(value) : value || ""
      );
    });

    if (imageFile) formData.append("profilePicture", imageFile);

    try {
      const res = await fetch(
        "https://core.pluritongues.com/merchants/partner",
        {
          method: "POST",
          body: formData,
        }
      );
      const result: ApiResponse = await res.json();
      const statusCode = result.statusCode;

      if (statusCode >= 400 && statusCode <= 499) {
        return toast.error(result.message);
      } else if (statusCode >= 500) {
        return toast.error(
          "Apologies, there was an error saving your record at the moment."
        );
      } else if (statusCode >= 200 && statusCode < 300) {
        toast.success("Account Registration Successful.");
        window.location.replace("https://www.pluritongues.com");
        setProfileImage("");
        return;
      } else {
        toast.info("Unexpected response. Please try again.");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Submission error:";
      toast.error(msg);
    } finally {
      setIsPending(false);
    }
  };

  const normalize = (str: string) => str.trim().toLowerCase();

  return (
    <div className={styles.regBox}>
      <div className={styles.header}>
        <div className={styles.logoWrap}>
          <SignUpLogo />
        </div>
        <h3>Register As A Language Partner</h3>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        {/* <h3>Basic Information</h3> */}

        {/* <div className={styles.divider}></div> */}

        <div className={styles.imgSect}>
          <div className={styles.imgCont}>
            <img
              src={profileImage || placeholder}
              alt="dp"
              className={styles.dpImage}
            />
          </div>
          <div className={styles.imgText}>
            {/* <p>
              This will be displayed to other users when they view your profile.
            </p> */}
            <input
              id="fileInput"
              hidden
              type="file"
              name="image"
              accept="image/*"
              className={styles.fileInput}
              onChange={handleImage}
            />
            <div
              className={styles.upload}
              onClick={() => document.getElementById("fileInput")?.click()}
            >
              <FontAwesomeIcon icon={faCameraAlt} />
              <span>Upload photo</span>
            </div>
            <div className={styles.imgNote}>
              <p>Accepted formats: JPG, PNG. Max file size 5MB.</p>
            </div>
          </div>
        </div>

        <div className={styles.formBody}>
          <Container>
            <Label htmlFor="firstName" title="First Name" />
            <Inputs
              id="firstName"
              name="firstName"
              type="text"
              placeholder="First name"
              value={userData.firstName}
              onChange={handleChange}
              required
            />
          </Container>

          <Container>
            <Label htmlFor="lastName" title="Last Name" />
            <Inputs
              id="lastName"
              name="lastName"
              type="text"
              placeholder="Last name"
              value={userData.lastName}
              onChange={handleChange}
              required
            />
          </Container>

          <Container>
            <Label htmlFor="otherNames" title="Other Names" />
            <Inputs
              id="otherNames"
              name="otherNames"
              type="text"
              placeholder="Other names"
              value={userData.otherNames}
              onChange={handleChange}
              required={false}
            />
          </Container>

          <Container>
            <Label htmlFor="email" title="Email" />
            <Inputs
              id="email"
              name="email"
              placeholder="Email"
              type="email"
              value={userData.email}
              onChange={handleChange}
              required
            />
          </Container>

          <Container>
            <Label htmlFor="dateOfBirth" title="Date of Birth" />
            <div className={styles.dateInputWrap}>
              <input
                id="dateOfBirth"
                type="date"
                name="dateOfBirth"
                placeholder="Date of Birth"
                className={styles.dateInput}
                value={userData?.dateOfBirth}
                onChange={handleChange}
                required
              />
            </div>
          </Container>
        </div>

        {/* Language Sections */}
        <div className={styles.langSection}>
          <p className={styles.sectionHeader}>Language Information</p>

          {/* Spoken Languages */}
          <Container>
            <Label htmlFor="spoken" title="Spoken Languages" />
            <div className={styles.selDiv}>
              <div className={styles.list}>
                {userData.languagesSpoken.map((item, index) => (
                  <div className={styles.spans} key={index}>
                    <span>{item.language}</span>
                    <span>-</span>
                    <span>{item.proficiency}</span>
                    <div
                      onClick={() => {
                        const updated = [...userData.languagesSpoken];
                        updated.splice(index, 1);
                        setUserData({ ...userData, languagesSpoken: updated });
                      }}
                    >
                      <Icon name="close" height={10} width={10} />
                    </div>
                  </div>
                ))}
              </div>
              <div className={styles.selects}>
                <Inputs
                  id="spoken"
                  name="language"
                  placeholder="Language you speak"
                  type="text"
                  value={selection.language}
                  required={false}
                  onChange={(e) =>
                    setSelection({ ...selection, language: e.target.value })
                  }
                />
                <select
                  name="proficiency"
                  value={selection.proficiency}
                  onChange={(e) =>
                    setSelection({ ...selection, proficiency: e.target.value })
                  }
                  className={styles.selection}
                >
                  <option>-Select Proficiency Level-</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="Native">Native Speaker</option>
                  <option value="Expert">Expert</option>
                </select>
              </div>

              <div className={styles.langFooter}>
                <Button
                  type="button"
                  variant="neutral"
                  className={styles.adLang}
                  onClick={() => {
                    const alreadySpoken = userData.languagesSpoken.some(
                      (item) =>
                        item.language.toLowerCase() ===
                        normalize(selection.language)
                    );

                    if (alreadySpoken) {
                      toast.error("You've already added this spoken language.");
                      return;
                    }

                    if (selection.language && selection.proficiency) {
                      setUserData({
                        ...userData,
                        languagesSpoken: [
                          ...userData.languagesSpoken,
                          selection,
                        ],
                      });
                      setSelection({ language: "", proficiency: "" });
                    }
                  }}
                >
                  Add Language
                </Button>
              </div>
            </div>
          </Container>

          <Container>
            <Label htmlFor="taught" title="Languages to Teach" />
            <div className={styles.selDiv}>
              <div className={styles.list}>
                {userData.languagesTaught.map((item, index) => (
                  <div className={styles.spans} key={index}>
                    <span>{item.language}</span>
                    <span>-</span>
                    <span>{item.proficiency}</span>
                    <div
                      onClick={() => {
                        const updated = [...userData.languagesTaught];
                        updated.splice(index, 1);
                        setUserData({ ...userData, languagesTaught: updated });
                      }}
                    >
                      <Icon name="close" height={10} width={10} />
                    </div>
                  </div>
                ))}
              </div>
              <div className={styles.selects}>
                <select
                  name="language"
                  value={selection2.language}
                  onChange={(e) =>
                    setSelection2({ ...selection2, language: e.target.value })
                  }
                  className={styles.selection}
                >
                  <option>Select Language</option>

                  {languages_taught?.map((item, index) => (
                    <option key={index} value={item.name}>
                      {item.name}
                    </option>
                  ))}
                </select>

                <select
                  name="proficiency"
                  value={selection2.proficiency}
                  onChange={(e) =>
                    setSelection2({
                      ...selection2,
                      proficiency: e.target.value,
                    })
                  }
                  className={styles.selection}
                >
                  <option>-Select Proficiency Level-</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="Native">Native Speaker</option>
                  <option value="Expert">Expert</option>
                </select>
              </div>

              <div className={styles.langFooter}>
                <Button
                  type="button"
                  variant="neutral"
                  className={styles.adLang}
                  onClick={() => {
                    const alreadyTaught = userData.languagesTaught.some(
                      (item) =>
                        item.language.toLowerCase() ===
                        normalize(selection2.language)
                    );

                    if (alreadyTaught) {
                      toast.error("You've already added this taught language.");
                      return;
                    }

                    if (selection2.language && selection2.proficiency) {
                      setUserData({
                        ...userData,
                        languagesTaught: [
                          ...userData.languagesTaught,
                          selection2,
                        ],
                      });
                      setSelection2({ language: "", proficiency: "" });
                    }
                  }}
                >
                  Add Language
                </Button>
              </div>
            </div>
          </Container>
        </div>

        <div className={styles.langSection}>
          <p className={styles.sectionHeader}>Summary</p>
          <Container>
            <Label htmlFor="aboutYou" title="About You" />
            <textarea
              id="aboutYou"
              name="aboutYou"
              placeholder="Tell us about yourself"
              value={userData.aboutYou}
              onChange={handleChange}
              className={styles.textarea}
              rows={6}
            />
          </Container>
        </div>

        <button className={styles.continue} disabled={isPending} type="submit">
          Submit
        </button>
      </form>
    </div>
  );
}
